import ImportMercureModel        from '@models/map/ImportMercureModel';
import PlanAnalysisResultModel   from '@models/map/PlanAnalysisResultModel';
import { EventSourcePolyfill }   from 'event-source-polyfill';

/** Étapes annoncées par le serveur, dans l'ordre (`PlanAnalysisRunner::STEPS`). */
export type PlanAnalysisStep = 'overview' | 'detection' | 'shapes' | 'glyphs' | 'doubts';

export type PlanAnalysisProgress = {
	step: PlanAnalysisStep;
	/** Rang de l'étape, à partir de 1. */
	current: number;
	total: number;
};

/** Ce que l'écran affiche pendant l'attente, étape par étape. */
export const PLAN_ANALYSIS_STEP_LABELS: Record<PlanAnalysisStep, string> = {
	overview: 'Lecture du plan',
	detection: 'Détection des calques',
	shapes: 'Extraction des formes',
	glyphs: 'Lecture des numéros',
	doubts: 'Recherche des points à vérifier',
};

/** Première relecture, puis on espace : une analyse courte doit rendre la main vite. */
const FIRST_POLL_MS = 1000;

/** Au-delà, Mercure prend le relais pour la réactivité ; le sondage n'est plus qu'un filet. */
const SLOW_POLL_MS = 5000;

/** Passage au rythme lent. */
const FAST_POLLS = 5;

/**
 * **Attend qu'une analyse de plan aboutisse**, et rend son résultat
 * (`docs/PLAN_ANALYSE_ASYNC.md` §4).
 *
 * ## Deux canaux, et un seul qui fait foi
 *
 * Mercure porte la PROGRESSION — le nom de l'étape en cours — et réveille la relecture dès que
 * l'analyse aboutit. Mais c'est la **relecture de l'analyse** qui décide : un message SSE perdu,
 * un hub absent en local, un onglet qui a dormi, et l'écran resterait devant un sablier
 * définitif si tout reposait sur le flux. Le sondage garantit donc le résultat ; Mercure ne fait
 * que le rendre immédiat et raconter l'attente.
 *
 * C'est aussi ce qui rend la fonction utilisable sans hub : en local, sans `MERCURE_PUBLISH_URL`,
 * l'analyse se termine exactement pareil, une seconde ou deux plus tard.
 *
 * ## Le jeton
 *
 * Les publications du service sont privées : il faut un jeton qui autorise le topic, et le topic
 * est celui de l'IMPORT. Un échec de cette requête n'est PAS une erreur d'analyse — on se
 * rabat silencieusement sur le sondage seul.
 */
export async function waitForPlanAnalysis(
	importId: number,
	planAnalysisId: number,
	options: { onProgress?: (progress: PlanAnalysisProgress) => void; signal?: AbortSignal } = {},
): Promise<PlanAnalysisResultModel> {
	const stream = await openStream(importId, planAnalysisId, options.onProgress);

	try {
		return await poll(planAnalysisId, stream.wake, options.signal);
	} finally {
		stream.close();
	}
}

/**
 * Relit l'analyse jusqu'à ce qu'elle aboutisse.
 *
 * `wake` est une promesse que le flux résout quand il annonce la fin : on attend soit elle, soit
 * le prochain tour de sondage, ce qui donne la réactivité de Mercure ET la garantie du sondage.
 */
async function poll(
	planAnalysisId: number,
	wake: () => Promise<void>,
	signal?: AbortSignal,
): Promise<PlanAnalysisResultModel> {
	for (let attempt = 0; ; attempt++) {
		if (signal?.aborted) throw new DOMException('Analyse abandonnée.', 'AbortError');

		const analysis = new PlanAnalysisResultModel();
		await analysis.setId(planAnalysisId).fetch();

		const status = analysis.planAnalysisStatus?.reference;

		if ('DONE' === status) return analysis;

		if ('ERROR' === status) {
			// Le motif est rédigé côté serveur POUR être affiché : « ce PDF est un scan »,
			// « il manque poppler ». Le remplacer par un message générique perdrait la seule
			// information qui dit quoi faire.
			throw new Error(analysis.errorMessage || 'L’analyse de ce plan a échoué.');
		}

		await Promise.race([wake(), sleep(attempt < FAST_POLLS ? FIRST_POLL_MS : SLOW_POLL_MS)]);
	}
}

/**
 * Ouvre le flux Mercure de l'import, ou rend un flux inerte si ce n'est pas possible.
 *
 * Inerte veut dire : `wake()` ne se résout jamais, donc le sondage garde son rythme propre. Rien
 * ne casse, l'attente est simplement moins bavarde.
 */
async function openStream(
	importId: number,
	planAnalysisId: number,
	onProgress?: (progress: PlanAnalysisProgress) => void,
): Promise<{ wake: () => Promise<void>; close: () => void }> {
	const endpoint = (window.APP_CONFIG?.services || [])
		.find(service => service.id === 'mercure')?.configurations.api_endpoint;

	if (!endpoint) return { wake: never, close: () => undefined };

	let resolveWake: (() => void) | null = null;
	const woken = new Promise<void>(resolve => {
		resolveWake = resolve;
	});

	try {
		const credentials = new ImportMercureModel();
		await credentials.setId(importId).fetch();

		const url = new URL(endpoint);
		url.searchParams.append('topic', credentials.topic);

		const source = new EventSourcePolyfill(url.toString().replace('/?', '?'), {
			headers: { Authorization: `Bearer ${credentials.token}` },
		});

		source.onmessage = (event: { data: string }) => {
			const message = readPlanAnalysisMessage(event.data, planAnalysisId);

			if (!message) return;

			if ('progress' === message.kind) {
				onProgress?.(message.progress);

				return;
			}

			resolveWake?.();
		};

		return { wake: () => woken, close: () => source.close() };
	} catch (e) {
		// Pas de jeton, pas de hub, pas de réseau : l'analyse se terminera quand même, le
		// sondage s'en charge. Ça mérite la console, pas un échec.
		console.warn('Fil Mercure de l’import indisponible, la relecture périodique prend le relais.', e);

		return { wake: never, close: () => undefined };
	}
}

type MercureMessage = { kind?: string; data?: Record<string, unknown> & { planAnalysisId?: number } };

/** Ce qu'un message du fil veut dire pour l'analyse qu'on attend, ou `null` s'il ne la concerne pas. */
export type PlanAnalysisMessage =
	| { kind: 'progress'; progress: PlanAnalysisProgress }
	| { kind: 'finished' };

/**
 * Interprète un message du fil de l'import.
 *
 * ⚠️ **Le fil porte TOUTES les analyses de cet import**, pas seulement celle qu'on attend :
 * l'opérateur qui relance après avoir corrigé sa zone a deux analyses en vol pendant quelques
 * secondes, et la fin de l'ancienne ne doit pas faire relire à la place de la nouvelle. D'où le
 * filtre sur `planAnalysisId`, qui est la seule chose qui les distingue.
 *
 * Un message illisible, d'un autre genre, ou sans identifiant, est ignoré sans bruit : le fil est
 * une commodité, c'est la relecture périodique qui fait foi.
 */
export function readPlanAnalysisMessage(raw: string, planAnalysisId: number): PlanAnalysisMessage | null {
	let payload: MercureMessage;

	try {
		payload = JSON.parse(raw) as MercureMessage;
	} catch {
		return null;
	}

	if (!payload?.data || payload.data.planAnalysisId !== planAnalysisId) return null;

	if ('plan_analysis.progress' === payload.kind) {
		const { step, current, total } = payload.data as unknown as PlanAnalysisProgress;

		return step ? { kind: 'progress', progress: { step, current, total } } : null;
	}

	if ('plan_analysis.done' === payload.kind || 'plan_analysis.failed' === payload.kind) {
		return { kind: 'finished' };
	}

	return null;
}

const never = () => new Promise<void>(() => undefined);

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));
